import * as React from "react"

import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcn/components/ui/table"

import { durationSeconds, previousRuns as labels, previousRunsLabel, runLabel } from "./copy"
import Disclosure from "./Disclosure"
import { VERDICT_TONE } from "./severity"
import type { MedicRunSummary } from "./types"

export default function MedicPreviousRuns({
  runs,
  currentToken,
  onSelect,
  renderWhen,
}: {
  runs?: MedicRunSummary[]
  currentToken?: string
  onSelect: (token: string) => void
  /*
   * Timestamps are rendered by the app's DateDisplay (which needs the user's
   * timezone from Redux) in the panel, and as a plain UTC string on the share
   * page, which has no user context. Injecting the renderer keeps this
   * component free of both dependencies.
   */
  renderWhen?: (run: MedicRunSummary) => React.ReactNode
}) {
  if (!runs || runs.length === 0) return null

  return (
    <Disclosure label={previousRunsLabel(runs.length)}>
      <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-card">
        <Table className="tw:min-w-[420px]">
          <TableHeader>
            <TableRow>
              <TableHead>{labels.colWhen}</TableHead>
              <TableHead>{labels.colVerdict}</TableHead>
              <TableHead className="tw:text-right">{labels.colDuration}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => {
              const isCurrent = run.token === currentToken
              const verdictKey = run.verdict ?? "pending"

              return (
                <TableRow
                  key={run.token}
                  onClick={() => onSelect(run.token)}
                  aria-current={isCurrent ? "true" : undefined}
                  className={cn(
                    "tw:cursor-pointer",
                    isCurrent && "tw:bg-secondary tw:hover:bg-secondary"
                  )}
                >
                  <TableCell>{renderWhen ? renderWhen(run) : run.createdTs}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={VERDICT_TONE[verdictKey]}
                    >
                      {runLabel(run.verdict, run.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    {run.durationMs && run.durationMs > 0
                      ? durationSeconds(run.durationMs)
                      : labels.noDuration}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Disclosure>
  )
}
